// Public bidder-registration boundary shared by listing and item bid controls.
// The server verifies saved-card readiness; hosted Stripe setup collects card
// details. A verified return never submits a bid automatically.
import { useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { trackEvent } from "../utils/analytics";
import { GA_EVENTS } from "../services/analytics/ga_events";

export const useAuctionRegistration = ({ eventId, required, user, onMessage }) => {
  const owner = `${eventId || ""}:${user?.uid || ""}`;
  const currentOwner = useRef(owner);
  currentOwner.current = owner;
  const notify = useRef(onMessage);
  notify.current = onMessage;
  const inFlight = useRef(false);
  const returnChecked = useRef({ key: "", promise: null });
  const [panel, setPanel] = useState({ owner: "", open: false, busy: false });
  const [checkingOwner, setCheckingOwner] = useState("");

  useEffect(() => {
    if (!required || !eventId || !user?.uid) return;
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get("auction_registration");
    const sessionId = params.get("session_id");
    const key = `${owner}:${outcome}:${sessionId}`;
    if (!outcome) return;
    if (outcome === "cancelled") {
      if (returnChecked.current.key === key) return;
      returnChecked.current = { key, promise: null };
      notify.current("warning", "Card registration was cancelled. Register before bidding; no bid was placed.");
      setPanel({ owner, open: true, busy: false });
      return;
    }
    if (outcome !== "success" || !sessionId) return;
    let active = true;
    setPanel({ owner, open: true, busy: true });
    // Share the verification promise across effect replays (React Strict Mode).
    const confirmation = returnChecked.current.key === key ? returnChecked.current.promise
      : httpsCallable(functions, "confirmAuctionRegistration")({ eventId, sessionId });
    returnChecked.current = { key, promise: confirmation };
    confirmation
      .then((result) => {
        if (!active || currentOwner.current !== owner) return;
        if (result.data?.registered !== true) throw new Error("Card registration is not complete. Please try again.");
        setPanel({ owner, open: false, busy: false });
        trackEvent({ action: GA_EVENTS.AUCTION_REGISTRATION_COMPLETED, parameters: { event_id: eventId, source_page: window.location.pathname } });
        notify.current("success", "Auction registration completed. Choose your bid again to continue.");
      })
      .catch((error) => {
        if (!active || currentOwner.current !== owner) return;
        setPanel({ owner, open: true, busy: false });
        notify.current("error", error.message || "Unable to verify card registration. Please try again.");
      });
    return () => { active = false; };
  }, [eventId, owner, required, user?.uid]);

  const checkBeforeBid = async () => {
    if (!required) return true;
    if (!user?.uid || !eventId || inFlight.current) return false;
    inFlight.current = true;
    setCheckingOwner(owner);
    try {
      const result = await httpsCallable(functions, "getAuctionRegistration")({ eventId });
      if (currentOwner.current !== owner) return false;
      if (result.data?.registered === true) {
        setPanel({ owner, open: false, busy: false });
        trackEvent({ action: GA_EVENTS.AUCTION_REGISTRATION_COMPLETED, parameters: { event_id: eventId, source_page: window.location.pathname } });
        return true;
      }
      setPanel({ owner, open: true, busy: false });
      return false;
    } catch (error) {
      if (currentOwner.current === owner) notify.current("error", error.message || "Unable to verify auction registration. Please try again before bidding.");
      return false;
    } finally {
      inFlight.current = false;
      setCheckingOwner("");
    }
  };

  const startRegistration = async ({ name, email, phone, consent, smsConsent }) => {
    if (!required || !user?.uid || !eventId || consent !== true) return;
    setPanel({ owner, open: true, busy: true });
    try {
      const result = await httpsCallable(functions, "createAuctionRegistrationSession")({
        eventId, name, email, phone, consent: true, smsConsent: smsConsent === true, returnPath: window.location.pathname,
      });
      if (currentOwner.current !== owner) return;
      if (result.data?.registered === true) {
        setPanel({ owner, open: false, busy: false });
        notify.current("success", "Auction registration completed. Choose your bid again to continue.");
        return;
      }
      const checkout = new URL(result.data?.url || "");
      if (checkout.protocol !== "https:" || checkout.hostname !== "checkout.stripe.com" || checkout.username || checkout.password) {
        throw new Error("Unable to open secure Stripe card registration. Please try again.");
      }
      trackEvent({ action: GA_EVENTS.AUCTION_REGISTRATION_STARTED, parameters: { event_id: eventId, source_page: window.location.pathname } });
      window.location.assign(checkout.href);
    } catch (error) {
      if (currentOwner.current === owner) {
        setPanel({ owner, open: true, busy: false });
        notify.current("error", error.message || "Unable to start secure card registration.");
      }
    }
  };

  return {
    checkBeforeBid,
    checking: checkingOwner === owner,
    panelProps: {
      open: required && panel.owner === owner && panel.open,
      busy: panel.owner === owner && panel.busy,
      user,
      onSubmit: startRegistration,
      onClose: () => setPanel({ owner, open: false, busy: false }),
    },
  };
};
